import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  ElsieAiModel,
  SetCitationsOrderPayload,
  SetCitationsPayload,
  SetConversationIdPayload,
  SetDisableAIOperationsPayload,
  SetIsPromptIdeasOpen,
  SetPromptIdeasPayload,
  SetTargetCitationPayload,
  SetTotalTokensPayload,
  ToggleCitationsPayload,
  SetElsieSummaryDataPayload,
  // Citation,
  SetTargetCitation2Payload,
  SetCitations2Payload,
  ChatHistoryState,
  SetChatHistoryConversationsPayload,
  SetCurrentConversationPayload,
  SetConversationMessagesPayload,
  ToggleChatHistoryPayload,
  SetChatHistoryLoadingPayload,
} from '../../models/elsie-ai.model';

// identifier
export const ELSI_AI_SLICE_ID = 'elsieAi';

// default chat history state
const initialChatHistoryState: ChatHistoryState = {
  conversations: [],
  currentConversationId: null,
  isHistoryLoading: false,
  isHistoryOpen: false,
  conversationMessages: {},
  lastLoadedConversation: null,
};

// default state
const initialState: ElsieAiModel = {
  conversationId: '',
  targetCitation: undefined,
  citations: [],
  citations2: [],
  citationsOrder: new Map(),
  isViewAllCitations: false,
  promptIdeas: undefined,
  isPromptIdeasOpen: false,
  isCitationsOpen: false,
  totalTokens: 0,
  disableAIOperations: false,
  elsieSummaryData: undefined,
  uploadedFiles: [],
  prompt: undefined,
  dataSources: [],
  chatHistory: initialChatHistoryState,
};

//slice
const elsieAiSlice = createSlice({
  name: ELSI_AI_SLICE_ID,
  initialState,
  reducers: {
    setConversationId: (state: ElsieAiModel, action: PayloadAction<SetConversationIdPayload>) => {
      state.conversationId = action.payload.conversationId;
    },
    setTargetCitation: (state: ElsieAiModel, action: PayloadAction<SetTargetCitationPayload>) => {
      state.targetCitation = action.payload.targetCitation;
    },
    setTargetCitation2: (state: ElsieAiModel, action: PayloadAction<SetTargetCitation2Payload>) => {
      state.targetCitation2 = action.payload.targetCitation2;
    },
    setCitations: (state: ElsieAiModel, action: PayloadAction<SetCitationsPayload>) => {
      state.citations = action.payload.citations;
    },
    setCitations2: (state: ElsieAiModel, action: PayloadAction<SetCitations2Payload>) => {
      state.citations2 = action.payload.citations;
    },
    setIsViewAllCitations: (state, action: PayloadAction<{ isViewAllCitations: boolean }>) => {
      state.isViewAllCitations = action.payload.isViewAllCitations;
    },
    setCitationsOrder: (state: ElsieAiModel, action: PayloadAction<SetCitationsOrderPayload>) => {
      state.citationsOrder = action.payload.citationsOrder;
    },
    setPromptIdeas: (state: ElsieAiModel, action: PayloadAction<SetPromptIdeasPayload>) => {
      state.promptIdeas = action.payload.promptIdeas;
    },
    setIsPromptIdeasOpen: (state: ElsieAiModel, action: PayloadAction<SetIsPromptIdeasOpen>) => {
      state.isPromptIdeasOpen = action.payload.isPromptIdeasOpen;
      state.isCitationsOpen = false;
    },
    toggleCitations: (state: ElsieAiModel, action: PayloadAction<ToggleCitationsPayload>) => {
      state.isCitationsOpen = action.payload.isCitationsOpen;
      state.isPromptIdeasOpen = false;
    },
    setTotalTokens: (state: ElsieAiModel, action: PayloadAction<SetTotalTokensPayload>) => {
      state.totalTokens = action.payload.totalTokens;
    },
    resetElsieStore: (state: ElsieAiModel) => {
      return {
        ...initialState,
        promptIdeas: state.promptIdeas,
        chatHistory: state.chatHistory,
      };
    },
    setDisableAIOperations: (
      state: ElsieAiModel,
      action: PayloadAction<SetDisableAIOperationsPayload>
    ) => {
      state.disableAIOperations = action.payload.numberOfResponseDetails == 0;
    },
    setElsieSummaryData: (
      state: ElsieAiModel,
      action: PayloadAction<SetElsieSummaryDataPayload>
    ) => {
      if (action.payload.kwelloIds) {
        state.elsieSummaryData = {
          referenceData: state?.elsieSummaryData?.referenceData || [],
          kwelloIds: action.payload.kwelloIds,
        };
      }
      if (action.payload.referenceData) {
        state.elsieSummaryData = {
          kwelloIds: state?.elsieSummaryData?.kwelloIds || [],
          referenceData: action.payload.referenceData || [],
        };
      }
    },
    clearUploadedFiles: (state: ElsieAiModel) => {
      state.uploadedFiles = [];
    },
    replaceUploadedFiles: (state: ElsieAiModel, action: PayloadAction<File[]>) => {
      state.uploadedFiles = [...action.payload];
    },
    setPrompt: (state: ElsieAiModel, action: PayloadAction<string>) => {
      state.prompt = action.payload;
    },
    clearPrompt: (state: ElsieAiModel) => {
      state.prompt = undefined;
    },
    setSelectedDataSources: (state, action: PayloadAction<string[]>) => {
      state.dataSources = action.payload;
    },

    // Chat History Reducers
    setChatHistoryConversations: (
      state: ElsieAiModel,
      action: PayloadAction<SetChatHistoryConversationsPayload>
    ) => {
      if (!action.payload.conversations?.length) return;

      // 1 Combine existing and new conversations
      const combined = [...state.chatHistory.conversations, ...action.payload.conversations];

      // 2 Keep only unique items by ID
      const unique = Array.from(new Map(combined.map((item) => [item.id, item])).values());

      // 3 Sort by timestamp (newest first)
      unique.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

      // 4 If "New Chat" exists, always move it to the top
      const newChatIndex = unique.findIndex((item) => item.title === 'New Chat');
      if (newChatIndex > 0) {
        const [newChat] = unique.splice(newChatIndex, 1);
        unique.unshift(newChat);
      }
      state.chatHistory.conversations = unique;
      state.chatHistory.isHistoryLoading = false;
    },

    setCurrentConversation: (
      state: ElsieAiModel,
      action: PayloadAction<SetCurrentConversationPayload>
    ) => {
      state.chatHistory.currentConversationId = action.payload.conversationId;
    },

    setConversationMessages: (
      state: ElsieAiModel,
      action: PayloadAction<SetConversationMessagesPayload>
    ) => {
      state.chatHistory.conversationMessages[action.payload.conversationId] =
        action.payload.messages;
      state.chatHistory.lastLoadedConversation = action.payload.conversationId;
    },

    toggleChatHistory: (state: ElsieAiModel, action: PayloadAction<ToggleChatHistoryPayload>) => {
      state.chatHistory.isHistoryOpen = action.payload.isHistoryOpen;
    },

    setChatHistoryLoading: (
      state: ElsieAiModel,
      action: PayloadAction<SetChatHistoryLoadingPayload>
    ) => {
      state.chatHistory.isHistoryLoading = action.payload.isLoading;
    },

    clearChatHistory: (state: ElsieAiModel) => {
      state.chatHistory = initialChatHistoryState;
    },

    removeChatHistoryConversation: (
      state: ElsieAiModel,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      state.chatHistory.conversations = state.chatHistory.conversations.filter(
        (conv) => conv.id !== action.payload.conversationId
      );
      delete state.chatHistory.conversationMessages[action.payload.conversationId];
      if (state.chatHistory.currentConversationId === action.payload.conversationId) {
        state.chatHistory.currentConversationId = null;
      }
    },
  },
});

//selectors
export const selectConversationId = (state: RootState) => state.elsieAi.conversationId;
export const selectTargetCitation = (state: RootState) => state.elsieAi.targetCitation;
export const selectTargetCitation2 = (state: RootState) => state.elsieAi.targetCitation2;
export const selectCitations = (state: RootState) => state.elsieAi.citations;
export const selectCitations2 = (state: RootState) => state.elsieAi.citations2;
export const selectCitationsOrder = (state: RootState) => state.elsieAi.citationsOrder;
export const selectPromptIdeas = (state: RootState) => state.elsieAi.promptIdeas;
export const selectIsPromptIdeasOpen = (state: RootState) => state.elsieAi.isPromptIdeasOpen;
export const selectIsCitationsOpen = (state: RootState) => state.elsieAi.isCitationsOpen;
export const selectTotalTokens = (state: RootState) => state.elsieAi.totalTokens;
export const selectDisableAIOperations = (state: RootState) => state.elsieAi.disableAIOperations;
export const selectElsieSummaryData = createSelector(
  [(state: RootState) => state.elsieAi],
  (state) => state.elsieSummaryData
);
export const selectPrompt = (state: RootState) => state.elsieAi.prompt;
export const selectSelectedDataSources = (state: RootState) => state.elsieAi.dataSources;

// Chat History Selectors
export const selectChatHistoryConversations = (state: RootState) =>
  state.elsieAi.chatHistory.conversations;
export const selectCurrentConversationId = (state: RootState) =>
  state.elsieAi.chatHistory.currentConversationId;
export const selectIsChatHistoryOpen = (state: RootState) =>
  state.elsieAi.chatHistory.isHistoryOpen;
export const selectIsChatHistoryLoading = (state: RootState) =>
  state.elsieAi.chatHistory.isHistoryLoading;
export const selectConversationMessages = (state: RootState) =>
  state.elsieAi.chatHistory.conversationMessages;
export const selectLastLoadedConversation = (state: RootState) =>
  state.elsieAi.chatHistory.lastLoadedConversation;

// Complex Chat History Selectors
export const selectCurrentConversationMessages = createSelector(
  [selectCurrentConversationId, selectConversationMessages],
  (currentId, allMessages) => {
    return currentId ? allMessages[currentId] : undefined;
  }
);

export const selectConversationById = createSelector(
  [selectChatHistoryConversations, (_state: RootState, conversationId: string) => conversationId],
  (conversations, conversationId) => {
    return conversations.find((conv) => conv.id === conversationId);
  }
);

//actions
export const {
  setConversationId,
  setTargetCitation,
  setTargetCitation2,
  setCitations,
  setCitations2,
  setCitationsOrder,
  setIsViewAllCitations,
  setPromptIdeas,
  toggleCitations,
  setIsPromptIdeasOpen,
  setTotalTokens,
  resetElsieStore,
  setDisableAIOperations,
  setElsieSummaryData,
  clearUploadedFiles,
  replaceUploadedFiles,
  setPrompt,
  clearPrompt,
  setSelectedDataSources,
  setChatHistoryConversations,
  setCurrentConversation,
  setConversationMessages,
  toggleChatHistory,
  setChatHistoryLoading,
  clearChatHistory,
  removeChatHistoryConversation,
} = elsieAiSlice.actions;

//reducer
export default elsieAiSlice;
